import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import { encryptBiometricImage } from '../utils/biometricEncryption';
import { Fingerprint, X, Plus } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { PROFILE_EDIT_TOKEN_KEY } from './ProfileUpdate';
import BiometricCapture from '../components/BiometricCapture';
import BiometricLivenessCapture from '../components/BiometricLivenessCapture';
import { useBiometricEnrollmentMode } from '../hooks/useBiometricEnrollmentMode';
import { getOnboardRegistrationErrorMessage } from '../utils/onboardRegistrationErrors';
import './Dashboard.css';
import './Registration.css';

function CaptureFaceModal({ profileEditToken, token, onSuccess, onClose, showError, showSuccess }) {
  const [capturedImage, setCapturedImage] = useState(null);
  const [livenessSessionId, setLivenessSessionId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { loading: modeLoading, useLiveness } = useBiometricEnrollmentMode();

  const handleCapture = (imageSrc) => {
    setCapturedImage(imageSrc);
    setLivenessSessionId(null);
    setError(null);
  };

  const handleLivenessComplete = (sessionId) => {
    setLivenessSessionId(sessionId);
    setCapturedImage(null);
    setError(null);
  };

  const biometricReady = useLiveness ? Boolean(livenessSessionId) : Boolean(capturedImage);

  const handleSubmit = async () => {
    if (!biometricReady) {
      setError(useLiveness ? 'Please complete live face verification first' : 'Please capture your face first');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const body = { profileEditToken };

      if (useLiveness && livenessSessionId) {
        const keyResponse = await axios.post(`${API_CONFIG.BASE_URL}/kms/generate-data-key`, {}, {
          timeout: 60000,
        });
        body.encryptedDataKey = keyResponse.data.ciphertext;
        body.livenessSessionId = livenessSessionId;
      } else {
        const base64Image = capturedImage.split(',')[1];
        const { encryptedDataKey, encryptedImageData } = await encryptBiometricImage(
          base64Image,
          API_CONFIG.BASE_URL
        );
        body.encryptedDataKey = encryptedDataKey;
        body.encryptedImageData = encryptedImageData;
      }

      await axios.post(`${API_CONFIG.BASE_URL}/user/update-biometric`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      showSuccess('Biometric updated successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      const errCode = err.response?.data?.error;
      if (errCode === 'profile_edit_token_required') {
        setError('Session expired. Please verify your security questions again.');
        showError('Session expired. Please verify your security questions again.');
      } else {
        const msg = getOnboardRegistrationErrorMessage(err);
        setError(msg);
        showError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setCapturedImage(null);
    setLivenessSessionId(null);
    setError(null);
  };

  return (
    <div className="biometric-modal-overlay" onClick={onClose}>
      <div className="biometric-modal" onClick={(e) => e.stopPropagation()}>
        <div className="biometric-modal-header">
          <h3>Update Face</h3>
          <button onClick={onClose} className="biometric-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        {modeLoading ? (
          <p className="biometric-capture-status">Loading…</p>
        ) : useLiveness && !livenessSessionId ? (
          <div className="biometric-modal-camera">
            <BiometricLivenessCapture
              onComplete={handleLivenessComplete}
              onError={(msg) => setError(msg)}
            />
          </div>
        ) : !capturedImage && !livenessSessionId ? (
          <div className="biometric-modal-camera">
            <BiometricCapture
              mode="capture"
              onCapture={handleCapture}
              containerClassName="webcam-container-update"
              captureButtonLabel="Capture Photo"
            />
          </div>
        ) : (
          <div className="biometric-modal-preview">
            {capturedImage ? <img src={capturedImage} alt="Captured" /> : <p>Live verification complete.</p>}
            <div className="biometric-modal-actions">
              <button onClick={handleRetry} disabled={isSubmitting} className="biometric-retry-btn">
                Retry
              </button>
              <button onClick={handleSubmit} disabled={isSubmitting} className="biometric-submit-btn">
                {isSubmitting ? 'Updating...' : 'Update Face'}
              </button>
            </div>
          </div>
        )}
        {error && <p className="biometric-modal-error">{error}</p>}
      </div>
    </div>
  );
}

function ProfileEdit() {
  const { user, token, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { showSuccess, showError } = useFlashMessage();

  const [profile, setProfile] = useState({ phoneNumber: '', email: '', fullName: '' });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [profileEditToken, setProfileEditToken] = useState(null);
  const [hasCheckedToken, setHasCheckedToken] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [ipWhite, setIpWhite] = useState(['']);
  const [ipBlack, setIpBlack] = useState(['']);
  const [ipLoading, setIpLoading] = useState(true);
  const [ipSaving, setIpSaving] = useState(false);
  const [ipLoadError, setIpLoadError] = useState(null);

  useEffect(() => {
    const storedToken = sessionStorage.getItem(PROFILE_EDIT_TOKEN_KEY);
    setHasCheckedToken(true);
    if (!storedToken) {
      showError('Please verify your security questions first.');
      navigate('/dashboard/profile-update');
      return;
    }
    setProfileEditToken(storedToken);
  }, [navigate, showError]);

  useEffect(() => {
    if (!token || !profileEditToken) return;
    setLoadingProfile(true);
    axios
      .get(`${API_CONFIG.BASE_URL}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data;
        setProfile(data);
        setPhoneNumber(data.phoneNumber || '');
        setEmail(data.email || '');
      })
      .catch(() => {
        showError('Failed to load profile');
      })
      .finally(() => setLoadingProfile(false));
  }, [token, profileEditToken, showError]);

  useEffect(() => {
    if (!token || !profileEditToken) return;
    setIpLoading(true);
    setIpLoadError(null);
    axios
      .get(`${API_CONFIG.BASE_URL}/user/ip-access`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const w = res.data.whitelisted_ips;
        const b = res.data.blocked_ips;
        setIpWhite(Array.isArray(w) && w.length ? [...w] : ['']);
        setIpBlack(Array.isArray(b) && b.length ? [...b] : ['']);
      })
      .catch((err) => {
        const code = err.response?.data?.error;
        if (code === 'user_not_found') {
          setIpLoadError('User record not found; IP lists are unavailable.');
        } else {
          setIpLoadError(err.response?.data?.message || 'Failed to load IP access');
        }
      })
      .finally(() => setIpLoading(false));
  }, [token, profileEditToken]);

  const handleSaveIpAccess = async (e) => {
    e.preventDefault();
    setIpSaving(true);
    try {
      await axios.put(
        `${API_CONFIG.BASE_URL}/user/ip-access`,
        {
          whitelisted_ips: ipWhite.map((s) => String(s).trim()).filter(Boolean),
          blocked_ips: ipBlack.map((s) => String(s).trim()).filter(Boolean),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('IP access updated.');
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showError(msg);
    } finally {
      setIpSaving(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      errors.email = 'Please enter a valid email address.';
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const hasChanges = phoneNumber !== (profile.phoneNumber || '') || email !== (profile.email || '');
    if (!hasChanges) {
      showError('No changes to save.');
      return;
    }

    setSavingProfile(true);
    try {
      await axios.put(
        `${API_CONFIG.BASE_URL}/user/profile`,
        {
          profileEditToken,
          ...(phoneNumber !== (profile.phoneNumber || '') && { phoneNumber }),
          ...(email !== (profile.email || '') && { email }),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Profile updated successfully!');
      setProfile((prev) => ({ ...prev, phoneNumber, email }));
    } catch (err) {
      const errCode = err.response?.data?.error;
      const msg = err.response?.data?.message || err.message;
      if (errCode === 'email_exists') {
        setValidationErrors((prev) => ({ ...prev, email: 'This email is already registered.' }));
      }
      if (errCode === 'profile_edit_token_required') {
        showError('Session expired. Please verify your security questions again.');
        sessionStorage.removeItem(PROFILE_EDIT_TOKEN_KEY);
        navigate('/dashboard/profile-update');
        return;
      }
      showError(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleBiometricSuccess = () => {
    setShowCaptureModal(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return null;
  }

  if (!hasCheckedToken || !profileEditToken) {
    return <div>Loading...</div>;
  }

  return (
    <DashboardLayout activeNav="profile-update">
        <div className="dashboard-section">
          <h2><Fingerprint size={24} /> Edit Profile</h2>
          <p className="update-biometric-description">
            Update your phone number, email address, or biometric (face image).
          </p>

          {loadingProfile ? (
            <p>Loading profile...</p>
          ) : (
            <form onSubmit={handleSaveProfile} className="update-biometric-form">
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                  className={validationErrors.phoneNumber ? 'input-error' : ''}
                />
                {validationErrors.phoneNumber && (
                  <span className="form-error">{validationErrors.phoneNumber}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setValidationErrors((prev) => { const next = { ...prev }; delete next.email; return next; }); }}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  className={validationErrors.email ? 'input-error' : ''}
                />
                {validationErrors.email && (
                  <span className="form-error">{validationErrors.email}</span>
                )}
              </div>
              <button type="submit" className="update-biometric-continue-btn" disabled={savingProfile}>
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          )}

          <div className="profile-edit-biometric-section" style={{ marginTop: '1.5rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>IP access</h3>
            <p className="update-biometric-description" style={{ marginBottom: '0.75rem' }}>
              Whitelist and block client IPs (IPv4, IPv6, or CIDR). Applied with tenant rules when you sign in.
            </p>
            {ipLoading ? (
              <p>Loading IP settings…</p>
            ) : ipLoadError ? (
              <p className="form-error" style={{ marginBottom: 0 }}>{ipLoadError}</p>
            ) : (
              <form onSubmit={handleSaveIpAccess} className="update-biometric-form">
                <div className="form-group">
                  <label>Whitelist IPs</label>
                  {ipWhite.map((row, i) => (
                    <div key={`w-${i}`} className="ip-access-row">
                      <input
                        type="text"
                        className="registration-input ip-access-input"
                        value={row}
                        onChange={(e) => {
                          const next = [...ipWhite];
                          next[i] = e.target.value;
                          setIpWhite(next);
                        }}
                        placeholder="e.g. 203.0.113.0/24"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="ip-access-remove-btn"
                        disabled={ipWhite.length <= 1}
                        onClick={() => setIpWhite(ipWhite.filter((_, j) => j !== i))}
                        aria-label="Remove row"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="ip-access-add-btn"
                    onClick={() => setIpWhite([...ipWhite, ''])}
                  >
                    <Plus size={18} strokeWidth={2.5} />
                    Add entry
                  </button>
                </div>
                <div className="form-group">
                  <label>Blocked IPs</label>
                  {ipBlack.map((row, i) => (
                    <div key={`b-${i}`} className="ip-access-row">
                      <input
                        type="text"
                        className="registration-input ip-access-input"
                        value={row}
                        onChange={(e) => {
                          const next = [...ipBlack];
                          next[i] = e.target.value;
                          setIpBlack(next);
                        }}
                        placeholder="e.g. 198.51.100.1"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="ip-access-remove-btn"
                        disabled={ipBlack.length <= 1}
                        onClick={() => setIpBlack(ipBlack.filter((_, j) => j !== i))}
                        aria-label="Remove row"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="ip-access-add-btn"
                    onClick={() => setIpBlack([...ipBlack, ''])}
                  >
                    <Plus size={18} strokeWidth={2.5} />
                    Add entry
                  </button>
                </div>
                <button type="submit" className="update-biometric-continue-btn" disabled={ipSaving}>
                  {ipSaving ? 'Saving…' : 'Save IP access'}
                </button>
              </form>
            )}
          </div>

          <div className="profile-edit-biometric-section" style={{ marginTop: '1.5rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Biometric (Face)</h3>
            <p className="update-biometric-description" style={{ marginBottom: '0.75rem' }}>
              Update your face image used for verification.
            </p>
            <button
              type="button"
              className="update-biometric-continue-btn"
              onClick={() => setShowCaptureModal(true)}
            >
              Update Face
            </button>
          </div>
        </div>

      {showCaptureModal && (
        <CaptureFaceModal
          profileEditToken={profileEditToken}
          token={token}
          onSuccess={handleBiometricSuccess}
          onClose={() => setShowCaptureModal(false)}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}
    </DashboardLayout>
  );
}

export default ProfileEdit;
